import { Box, Stack, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { PortalAwardOutcomeDto, type PortalAwardLetterDto, type PortalSourcingEventDetailDto } from "../../../../autogen/bff-api";
import { RejectionDetails } from "../../../../common/rejection/RejectionDetails";
import { AwardStatutoryNotices } from "./AwardStatutoryNotices";
import { portalStandstillTerms } from "./portalStandstillTerms";
import { awardOutcomeMessage } from "./awardOutcomeMessages";
import { isPrivatePortalEvent } from "./PrivateInviteCta";
import { isDesignContest } from "../../buyer/wizard/utils/designContest";

interface Props {
  event: PortalSourcingEventDetailDto;
  award: PortalAwardLetterDto;
}

export const AwardOutcomeBanner = ({ event, award }: Props) => {
  const { t } = useTranslation();
  const message = awardOutcomeMessage(
    award,
    isPrivatePortalEvent(event),
    t,
    isDesignContest(event.procedureType),
    event.designContest?.leadsToServiceContract === true,
  );
  if (!message) return null;

  const isWinner = award.outcome === PortalAwardOutcomeDto.Winner;
  const isWithdrawn = award.outcome === PortalAwardOutcomeDto.Withdrawn;

  return (
    <Box
      bg={isWinner ? "smPrimarySofter" : "smBackgroundSecondary"}
      border="1px solid"
      borderColor="smBorder"
      rounded="xl"
      p="5"
      data-testid="award-outcome-banner"
    >
      <Stack spacing="2">
        <Text fontSize="lg" fontWeight="semibold" color="smText">
          {message.title}
        </Text>
        {message.body && (
          <Text fontSize="sm" color="smText">
            {message.body}
          </Text>
        )}
        {message.ranking && (
          <Text fontSize="sm" color="smText">
            {message.ranking}
          </Text>
        )}
        {award.outcome === PortalAwardOutcomeDto.Rejected && award.rejectionMessage && (
          <RejectionDetails message={award.rejectionMessage} />
        )}
        {!isWithdrawn && !award.letter && (
          <AwardStatutoryNotices
            event={event}
            regulation={award.regulation ?? null}
            standstillExpiresAt={award.standstillExpiresAt}
            standstill={portalStandstillTerms(award)}
          />
        )}
      </Stack>
    </Box>
  );
};
